import { useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTellescopeInfiniteQuery, useTellescopeMutation, useUploadTellescopeFile } from "@tellescope/tanstack-query";
import { TellescopeFormFieldBaseProps } from "../../types";
import { normalizeValidFileTypes } from "./normalizeValidFileTypes";
import { isFileTypeAllowed } from "./isFileTypeAllowed";

export interface FileFieldExtraProps {
  multiple?: boolean;
  validFileTypes?: string;
  maxSizeMB?: number;
  maxFiles?: number;
  enduserId?: string;
  autoUploadFiles?: boolean;
  hideFromPortal?: boolean;
  pendingFiles?: File[];
  onPendingFilesChange?: (files: File[]) => void;
}

export interface FileFieldProps extends TellescopeFormFieldBaseProps<"files">, FileFieldExtraProps {}

type UploadedValueItem = NonNullable<TellescopeFormFieldBaseProps<"files">["value"]>[number];

export interface FileFieldLogicArgs {
  value: TellescopeFormFieldBaseProps<"files">["value"];
  onChange: TellescopeFormFieldBaseProps<"files">["onChange"];
  validFileTypes?: string;
  multiple: boolean;
  maxSizeMB: number;
  maxFiles?: number;
  enduserId?: string;
  autoUploadFiles: boolean;
  hideFromPortal?: boolean;
  pendingFiles?: File[];
  onPendingFilesChange?: (files: File[]) => void;
}

const isFileSizeValid = (file: File, maxSizeMB: number): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

export const useFileFieldLogic = ({
  value,
  onChange,
  validFileTypes,
  multiple,
  maxSizeMB,
  maxFiles,
  enduserId,
  autoUploadFiles,
  hideFromPortal,
  pendingFiles,
  onPendingFilesChange,
}: FileFieldLogicArgs) => {
  const { t } = useTranslation("forms");
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [erroredFiles, setErroredFiles] = useState<File[]>([]);
  const [filesBeingUploaded, setFilesBeingUploaded] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Concurrent uploads each need the value as of their own completion, not as of when they
  // started, or a batch finishing second drops the first batch's files from the response.
  const latestValueRef = useRef(value);
  latestValueRef.current = value;

  const secureNames = value?.map(({ secureName }) => secureName);

  const uploadedFilesQuery = useTellescopeInfiniteQuery({
    model: "files",
    method: "getSome",
    request: {
      filter: {
        secureName: {
          _in: secureNames,
        },
      },
    },
    select: (data) => data.pages.flat(),
  });

  const uploadedFiles = useMemo(() => {
    const currentSecureNames = value?.map((f) => f.secureName);
    const filtered = uploadedFilesQuery.data?.filter((f) => currentSecureNames?.includes(f.secureName));
    const sortable = [...(filtered?.length ? filtered : value ?? [])];
    return sortable.sort((a, b) => a.name.localeCompare(b.name));
  }, [uploadedFilesQuery.data, value]);

  const deleteFileMutation = useTellescopeMutation({
    model: "files",
    method: "deleteOne",
    autoCacheUpdateStrategy: "update",
  });

  const { uploadFileAsync } = useUploadTellescopeFile();

  const normalizedFileTypes = validFileTypes ? normalizeValidFileTypes(validFileTypes) : undefined;

  const hasReachedMaxFiles =
    multiple &&
    maxFiles !== undefined &&
    maxFiles > 1 &&
    (value?.length ?? 0) + (pendingFiles?.length ?? 0) >= maxFiles;

  const validateAndExtract = (files: FileList | File[]): { validFiles: File[]; errorMessage: string | null } => {
    const fileArray = Array.from(files);
    const invalidFile = fileArray.find(
      (file) => !isFileTypeAllowed(file, normalizedFileTypes) || !isFileSizeValid(file, maxSizeMB)
    );

    if (!invalidFile) {
      return { validFiles: fileArray, errorMessage: null };
    }

    return {
      validFiles: fileArray.slice(0, fileArray.indexOf(invalidFile)),
      errorMessage: !isFileTypeAllowed(invalidFile, normalizedFileTypes)
        ? t("Invalid file type")
        : t("File too large (max {{maxSize}}MB)", { maxSize: maxSizeMB }),
    };
  };

  const queuePendingFiles = (validFiles: File[]) => {
    if (!onPendingFilesChange) return;
    const current = pendingFiles ?? [];
    const next = multiple ? [...current, ...validFiles] : validFiles.slice(0, 1);
    onPendingFilesChange(next);
  };

  const uploadAndCommit = async (validFiles: File[]) => {
    if (!enduserId) {
      setError(t("Cannot upload files without enduserId"));
      return;
    }

    setFilesBeingUploaded((prev) => [...prev, ...validFiles]);

    const responses = await Promise.all(
      validFiles.map(async (file) => {
        try {
          const res = await uploadFileAsync(
            { enduserId, name: file.name, size: file.size, type: file.type, hiddenFromEnduser: hideFromPortal },
            file
          );
          return { uploadedFile: res, originalFile: file };
        } catch {
          return { originalFile: file };
        }
      })
    );

    const { successful, failed } = responses.reduce<{ successful: UploadedValueItem[]; failed: File[] }>(
      (acc, r) => {
        if (r.uploadedFile) {
          acc.successful.push({
            name: r.uploadedFile.name,
            secureName: r.uploadedFile.secureName,
            type: multiple ? r.uploadedFile.type : "file",
          });
        } else {
          acc.failed.push(r.originalFile);
        }
        return acc;
      },
      { successful: [], failed: [] }
    );

    const currentValue = latestValueRef.current;

    if (multiple) {
      if (successful.length > 0) {
        onChange([...(currentValue ?? []), ...successful]);
      }
    } else {
      const next = successful[0];
      if (next) {
        onChange([next]);
        const oldSecureName = currentValue?.[0]?.secureName;
        if (oldSecureName && oldSecureName !== next.secureName) {
          const oldFileId = uploadedFilesQuery.data?.find((f) => f.secureName === oldSecureName)?.id;
          if (oldFileId) {
            deleteFileMutation.mutate(oldFileId);
          }
        }
      }
    }

    setErroredFiles((prev) => [...prev, ...failed]);
    setFilesBeingUploaded((prev) => prev.filter((file) => !validFiles.includes(file)));
  };

  const handleNewFiles = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;

    const { validFiles, errorMessage } = validateAndExtract(files);
    if (errorMessage) {
      setError(errorMessage);
      return;
    }
    setError(null);

    if (!autoUploadFiles) {
      queuePendingFiles(validFiles);
      return;
    }

    await uploadAndCommit(validFiles);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (hasReachedMaxFiles) {
      setError(t("Maximum files reached. Remove a file before adding more."));
      return;
    }
    handleNewFiles(e.dataTransfer.files);
  };

  const handleClick = () => {
    if (hasReachedMaxFiles) {
      setError(t("Maximum files reached. Remove a file before adding more."));
      return;
    }
    fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleNewFiles(e.target.files);
    e.target.value = "";
  };

  const handleRemoveUploadedFile = (secureName: string) => {
    const newFiles = value?.filter((file) => secureName !== file.secureName);
    onChange(newFiles);
    if (autoUploadFiles) {
      const fileId = uploadedFilesQuery.data?.find((file) => file.secureName === secureName)?.id;
      if (fileId) {
        deleteFileMutation.mutate(fileId);
      }
    }
    setError(null);
  };

  const handleRemovePendingFile = (index: number) => {
    if (!onPendingFilesChange) return;
    const current = pendingFiles ?? [];
    onPendingFilesChange([...current.slice(0, index), ...current.slice(index + 1)]);
  };

  const handleRemoveErroredFile = (index: number) => {
    setErroredFiles((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
  };

  const handleRetryUpload = (file: File, index: number) => {
    setErroredFiles((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
    handleNewFiles([file]);
  };

  const inFlightFiles = autoUploadFiles ? filesBeingUploaded : pendingFiles ?? [];
  const displayedUploadedFiles = !multiple && inFlightFiles.length > 0 ? [] : uploadedFiles;
  const fileListCount = displayedUploadedFiles.length + inFlightFiles.length + erroredFiles.length;
  const showFileList = fileListCount > 0;

  return {
    isDragging,
    error,
    erroredFiles,
    inFlightFiles,
    inFlightVariant: autoUploadFiles ? ("uploading" as const) : ("pending" as const),
    fileInputRef,
    normalizedFileTypes,
    displayedUploadedFiles,
    fileListCount,
    showFileList,
    multiple,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleClick,
    handleInputChange,
    handleRemoveUploadedFile,
    handleRemovePendingFile,
    handleRemoveErroredFile,
    handleRetryUpload,
  };
};
